import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

// ============================================================
// Everyone we know at one institution
// ============================================================
// A school is not one contact. The receptionist who takes the first call, the
// coordinator who forwards the proposal, the vice principal who sits in on the
// demo and the principal who signs are four different people, and a rep who
// only has one of them is stuck the day that person is on leave.
//
// Contacts are never hard-deleted. Removing one hides it; the previous values
// of an edited contact are kept on the server. A phone number typed over by
// mistake in a corridor can be recovered, which is not true of a DELETE.

const EMPTY = { name: "", designation: "", department: "", phone: "", email: "", whatsapp: "", is_primary: false };

const DESIGNATIONS = [
  "Principal", "Correspondent", "Chairman", "Director", "Vice Principal",
  "Coordinator", "Administrator", "HOD", "Receptionist", "Accounts", "Other",
];

export default function InstitutionContacts({ institutionId, institutionName, accent = "#0f9f9a" }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    if (!institutionId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await API.get(`/institutions/${institutionId}/contacts`);
      setContacts(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load contacts");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => { load(); }, [load]);

  const startAdd = () => { setEditingId(null); setDraft(EMPTY); setAdding(true); };
  const startEdit = (contact) => {
    setAdding(false);
    setEditingId(contact.id);
    setDraft({
      name: contact.name || "", designation: contact.designation || "",
      department: contact.department || "", phone: contact.phone || "",
      email: contact.email || "", whatsapp: contact.whatsapp || "",
      is_primary: !!contact.is_primary,
    });
  };
  const cancel = () => { setAdding(false); setEditingId(null); setDraft(EMPTY); };

  const save = async () => {
    if (!draft.name.trim()) return toast.error("Contact name is required");
    if (draft.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(draft.email.trim())) {
      return toast.error("That email address does not look right");
    }
    setSaving(true);
    try {
      if (editingId) {
        await API.put(`/institutions/${institutionId}/contacts/${editingId}`, draft);
        toast.success("Contact updated");
      } else {
        await API.post(`/institutions/${institutionId}/contacts`, draft);
        toast.success(`${draft.name.trim()} added`);
      }
      cancel();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not save the contact");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (contact) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove ${contact.name} from ${institutionName || "this institution"}?\n\nThey stay in the history and can be added again.`)) return;
    try {
      await API.delete(`/institutions/${institutionId}/contacts/${contact.id}`);
      toast.success(`${contact.name} removed`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not remove the contact");
    }
  };

  const input = {
    width: "100%", padding: "10px 12px", borderRadius: 10, border: "1.5px solid #e2e8f0",
    fontSize: 14, fontFamily: "inherit", outline: "none", background: "#fff", boxSizing: "border-box",
  };
  const label = { fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: 0.4 };
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 9 };

  if (!institutionId) {
    return (
      <div style={{ padding: "12px 14px", borderRadius: 12, background: "#f8fafc", color: "#64748b", fontSize: 12, lineHeight: 1.6 }}>
        This lead is not linked to an institution record yet. Log a visit and it will be linked automatically.
      </div>
    );
  }

  const form = (
    <div style={{ border: `1.5px solid ${accent}`, borderRadius: 13, padding: 13, background: "#f0fdfa", marginBottom: 10 }}>
      <div style={{ ...grid, marginBottom: 9 }}>
        <div>
          <label style={label}>Name *</label>
          <input style={input} value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="Mrs. Rajam" />
        </div>
        <div>
          <label style={label}>Designation</label>
          <select style={{ ...input, appearance: "none", cursor: "pointer" }} value={draft.designation}
            onChange={e => setDraft(d => ({ ...d, designation: e.target.value }))}>
            <option value="">Select</option>
            {DESIGNATIONS.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
      </div>
      <div style={{ ...grid, marginBottom: 9 }}>
        <div>
          <label style={label}>Mobile</label>
          <input style={input} type="tel" value={draft.phone} onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))} placeholder="+91 XXXXX XXXXX" />
        </div>
        <div>
          <label style={label}>Email</label>
          <input style={input} type="email" value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))} placeholder="name@school.in" />
        </div>
      </div>
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", marginBottom: 11, cursor: "pointer" }}>
        <input type="checkbox" checked={draft.is_primary} onChange={e => setDraft(d => ({ ...d, is_primary: e.target.checked }))} />
        Main contact for this institution
      </label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" onClick={save} disabled={saving}
          style={{ flex: 1, padding: "11px 14px", border: 0, borderRadius: 10, background: accent, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
          {saving ? "Saving..." : editingId ? "Save changes" : "Add contact"}
        </button>
        <button type="button" onClick={cancel}
          style={{ padding: "11px 16px", border: "1px solid #cbd5e1", borderRadius: 10, background: "#fff", color: "#475569", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: "#1e293b" }}>
          Contacts {contacts.length > 0 && <span style={{ color: "#94a3b8", fontWeight: 600 }}>({contacts.length})</span>}
        </div>
        {!adding && !editingId && (
          <button type="button" onClick={startAdd}
            style={{ border: `1.5px solid ${accent}`, borderRadius: 9, background: "#fff", color: accent, padding: "6px 11px", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>
            + Add contact
          </button>
        )}
      </div>

      {adding && form}

      {loading ? (
        <div style={{ padding: 16, textAlign: "center", color: "#64748b", fontSize: 13 }}>Loading contacts...</div>
      ) : contacts.length === 0 && !adding ? (
        <div style={{ padding: "14px", borderRadius: 12, background: "#f8fafc", color: "#64748b", fontSize: 12, lineHeight: 1.6 }}>
          Nobody recorded here yet. Add the reception desk first — it is the number everyone else is reached through.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 8 }}>
          {contacts.map(contact => (
            editingId === contact.id ? <div key={contact.id}>{form}</div> : (
              <div key={contact.id} style={{ border: "1px solid #e2e8f0", borderRadius: 12, padding: "11px 13px", background: "#fff" }}>
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 750, color: "#172033" }}>
                      {contact.name}
                      {!!contact.is_primary && (
                        <span style={{ marginLeft: 7, padding: "2px 7px", borderRadius: 20, background: "#f0fdfa", color: accent, fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0.4 }}>Main</span>
                      )}
                    </div>
                    {(contact.designation || contact.department) && (
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                        {[contact.designation, contact.department].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: "#475569", marginTop: 5, lineHeight: 1.6, wordBreak: "break-word" }}>
                      {contact.phone && <div><a href={`tel:${contact.phone}`} style={{ color: accent, textDecoration: "none", fontWeight: 700 }}>{contact.phone}</a></div>}
                      {contact.email && <div><a href={`mailto:${contact.email}`} style={{ color: "#475569", textDecoration: "none" }}>{contact.email}</a></div>}
                      {!contact.phone && !contact.email && <div style={{ color: "#94a3b8" }}>No number or email yet</div>}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <button type="button" onClick={() => startEdit(contact)}
                      style={{ border: "1px solid #cbd5e1", borderRadius: 8, background: "#fff", color: "#475569", padding: "5px 10px", fontSize: 11, fontWeight: 750, cursor: "pointer", fontFamily: "inherit" }}>
                      Edit
                    </button>
                    <button type="button" onClick={() => remove(contact)}
                      style={{ border: "1px solid #fecaca", borderRadius: 8, background: "#fff", color: "#b91c1c", padding: "5px 10px", fontSize: 11, fontWeight: 750, cursor: "pointer", fontFamily: "inherit" }}>
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );
}
